/**
 * Owner: Masa Bayu
 * File: batch05-fixtures.ts
 * Date:Time: 2026-09-20 00:25
 * Description: Batch 5 certification fixtures — Indic shaping / conjuncts.
 */
import { QX_BATCH_05_FAMILY_IDS, } from './batch05-codes';
const SCRIPT_LABEL = Object.freeze({
    devanagari: 'Deva',
    bengali: 'Beng',
    gurmukhi: 'Guru',
    gujarati: 'Gujr',
    odia: 'Orya',
    tamil: 'Taml',
    telugu: 'Telu',
    kannada: 'Knda',
    malayalam: 'Mlym',
    sinhala: 'Sinh',
});
function fixturesFor(familyId) {
    const scriptLabel = SCRIPT_LABEL[familyId];
    return Object.freeze([
        Object.freeze({
            id: `fixture:${familyId}/identity`,
            familyId,
            area: 'identity',
            description: `canonical languageId samples for ${familyId}`,
        }),
        Object.freeze({
            id: `fixture:${familyId}/script-character`,
            familyId,
            area: 'script',
            description: `${scriptLabel} abugida · combining marks · conjunct identity`,
        }),
        Object.freeze({
            id: `fixture:${familyId}/direction-ltr`,
            familyId,
            area: 'direction',
            description: 'LTR default + explicit ltr override',
        }),
        Object.freeze({
            id: `fixture:${familyId}/content-multilingual`,
            familyId,
            area: 'content',
            description: 'primary + contentLanguages',
        }),
        Object.freeze({
            id: `fixture:${familyId}/qxw-author-preserve`,
            familyId,
            area: 'qxw',
            description: 'QXW root metadata author → preserve',
        }),
        Object.freeze({
            id: `fixture:${familyId}/qxp-publish-preserve`,
            familyId,
            area: 'qxp',
            description: 'QXP artifact preserve-through-publish',
        }),
        Object.freeze({
            id: `fixture:${familyId}/reader-typography`,
            familyId,
            area: 'reader',
            description: 'Indic complex shaping · ligature/conjunct stacks',
        }),
        Object.freeze({
            id: `fixture:${familyId}/search-indic-segment`,
            familyId,
            area: 'search',
            description: 'NFC + per-script analyzer · script_aware · forbid whitespace-only default',
        }),
        Object.freeze({
            id: `fixture:${familyId}/navigation-structure`,
            familyId,
            area: 'navigation',
            description: 'writing mode + heading role metadata',
        }),
        Object.freeze({
            id: `fixture:${familyId}/annotation-anchors`,
            familyId,
            area: 'annotation',
            description: 'original text preserved for selection/annotation anchors',
        }),
        Object.freeze({
            id: `fixture:${familyId}/a11y-lang-semantics`,
            familyId,
            area: 'accessibility',
            description: 'content lang + reading order · combining-mark safe',
        }),
        Object.freeze({
            id: `fixture:${familyId}/serialize-roundtrip`,
            familyId,
            area: 'serialization',
            description: 'metadata / content round-trip',
        }),
        Object.freeze({
            id: `fixture:${familyId}/regression-suite`,
            familyId,
            area: 'regression',
            description: 'content id stable under override',
        }),
        Object.freeze({
            id: `fixture:${familyId}/provenance-chain`,
            familyId,
            area: 'provenance',
            description: 'source → evidence area trace',
        }),
    ]);
}
const BY_FAMILY = Object.freeze(Object.fromEntries(QX_BATCH_05_FAMILY_IDS.map((id) => [id, fixturesFor(id)])));
export const QX_BATCH_05_FIXTURES = Object.freeze(QX_BATCH_05_FAMILY_IDS.flatMap((id) => [...BY_FAMILY[id]]));
export const QX_BATCH_05_SAMPLE_LANGUAGES = Object.freeze({
    devanagari: Object.freeze(['hi', 'mr', 'ne', 'sa']),
    bengali: Object.freeze(['bn', 'as']),
    gurmukhi: Object.freeze(['pa']),
    gujarati: Object.freeze(['gu']),
    odia: Object.freeze(['or']),
    tamil: Object.freeze(['ta']),
    telugu: Object.freeze(['te']),
    kannada: Object.freeze(['kn']),
    malayalam: Object.freeze(['ml']),
    sinhala: Object.freeze(['si']),
});
/** Samples include conjunct / combining-mark orthography where applicable. */
export const QX_BATCH_05_TEXT_SAMPLES = Object.freeze({
    hi: 'नमस्ते विश्व—क्षत्रिय प्रमाणन नमूना',
    bn: 'নমস্কার বিশ্ব—বাংলা প্রমাণীকরণ নমুনা',
    pa: 'ਸਤ ਸ੍ਰੀ ਅਕਾਲ—ਪੰਜਾਬੀ ਪ੍ਰਮਾਣੀਕਰਨ ਨਮੂਨਾ',
    gu: 'નમસ્તે વિશ્વ—ગુજરાતી પ્રમાણન નમૂનો',
    or: 'ନମସ୍କାର ବିଶ୍ୱ—ଓଡ଼ିଆ ପ୍ରମାଣୀକରଣ ନମୁନା',
    ta: 'வணக்கம் உலகம்—தமிழ் சான்றிதழ் மாதிரி',
    te: 'నమస్కారం ప్రపంచం—తెలుగు ధృవీకరణ నమూనా',
    kn: 'ನಮಸ್ಕಾರ ವಿಶ್ವ—ಕನ್ನಡ ಪ್ರಮಾಣೀಕರಣ ಮಾದರಿ',
    ml: 'നമസ്കാരം ലോകം—മലയാളം സർട്ടിഫിക്കേഷൻ സാമ്പിൾ',
    si: 'ආයුබෝවන් ලෝකය—සිංහල සහතික නියැදිය',
    composed: 'cafe\u0301',
});
export function listBatch05FixtureIds(familyId) {
    const list = familyId ? BY_FAMILY[familyId] : QX_BATCH_05_FIXTURES;
    return Object.freeze(list.map((f) => f.id));
}
export function fixturesForBatch05Family(familyId) {
    return BY_FAMILY[familyId];
}
//# sourceMappingURL=batch05-fixtures.js.map